import { GatewayRegistrationCode } from "./GatewayRegistrationCode";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { LocalGatewayInstallerDownload } from "./LocalGatewayInstallerDownload";
import { StorageDeleteControl } from "./StorageDeleteControl";
import { storageKindLabel } from "@/lib/storage-kind-label";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { StorageUploadLimitForm } from "./StorageUploadLimitForm";
import {
  useCallback,
  useState,
  type FormEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ExpandablePanel } from "@/components/shared/ExpandablePanel";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { StatusBadge } from "@/components/shared/StatusBadge";
import {
  useStorageDetail,
  useIssueLocalGatewayRouteHandoff,
  useIssueLocalGatewaySetupHandoff,
  useIssuedLocalGatewayHandoffPolling,
  type IssuedLocalGatewayHandoffPollingOptions,
  useLocalSetupPolling,
  usePutStorageCredentialMaterial,
  useResetStorageCredential,
  useDeleteAllLocalGatewayRoutes,
  useDeleteLocalGatewayRoute,
  useUpdateStorageDisplayName,
  useUpdateSmbRoot,
  useVerifyStorageCredential,
} from "./hooks/useStorages";
import { normalizeSmbBasePathInput } from "./smb-root";
import {
  getLocalGatewayRouteLimit,
  isLocalCapableStorage,
  resolveLocalStorageProgressSteps,
  resolveStorageActionCode,
} from "./storage-ux";
import { GatewayRouteSummaryPanel } from "./GatewayRouteSummaryPanel";
import { StorageConnectionKindIconBadge } from "./StorageConnectionKindIcon";
import { StorageSettingStatusBadge } from "./StorageSettingStatusBadge";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  KeyRound,
  Loader2,
  Pencil,
  RefreshCw,
  Trash2,
  Unlink2,
} from "lucide-react";
import type {
  LocalGatewayHandoffResponse,
  StorageDetailResponse,
  StorageCredentialStateResponse,
  StorageReadinessResponse,
  StorageRuntimeStatusSnapshot,
} from "@/types/api";
import { ApiError } from "@/api/client";
import { copyTextToClipboard } from "@/lib/clipboard";
import { useLocalGatewayHandoffMemory } from "./LocalGatewayHandoffContext";

type CredentialActionFeedbackTone = "success" | "attention";
type CredentialVerificationTone = "success" | "attention" | "neutral";
type StorageDetailSettingState = "ready" | "pending" | "blocked" | "unknown" | "disabled";
type StorageDetailAvailabilityCode =
  | "available"
  | "setup_incomplete"
  | "needs_action"
  | "unavailable"
  | "needs_check";
type GatewayRoute = NonNullable<
  NonNullable<StorageDetailResponse["gatewayRouteSummary"]>["routes"]
>[number];

const STORAGE_DISPLAY_NAME_MAX_LENGTH = 120;
const AVAILABILITY_BADGE_STATE: Record<StorageDetailAvailabilityCode, string> = {
  available: "ready",
  setup_incomplete: "pending",
  needs_action: "blocked",
  unavailable: "unavailable",
  needs_check: "degraded",
};

function resolveRuntimeStatusLineCode(
  runtimeStatus: StorageRuntimeStatusSnapshot | null | undefined,
) {
  if (!runtimeStatus) return "not_requested";
  if (runtimeStatus.source === "pf_unavailable") {
    return "fetch_failed";
  }
  if (runtimeStatus.source === "not_requested") {
    return "not_requested";
  }
  if (
    runtimeStatus.source === "not_observed" ||
    runtimeStatus.runtimeStatusState === "not_requested"
  ) {
    return "not_observed";
  }
  return runtimeStatus.runtimeStatusState;
}

function runtimeStatusBadgeState(
  code: ReturnType<typeof resolveRuntimeStatusLineCode>,
) {
  switch (code) {
    case "available":
      return "ready";
    case "unavailable":
      return "unavailable";
    case "degraded":
    case "fetch_failed":
    case "unknown":
      return "degraded";
    default:
      return "disabled";
  }
}

function resolveStorageSettingState(
  storage: Pick<StorageDetailResponse, "storageLifecycleState" | "settingState">,
): StorageDetailSettingState {
  if (storage.storageLifecycleState !== "active") {
    return "disabled";
  }
  return storage.settingState as StorageDetailSettingState;
}

function resolveStorageAvailabilityCode(
  settingState: StorageDetailSettingState,
  runtimeState: ReturnType<typeof resolveRuntimeStatusLineCode>,
): StorageDetailAvailabilityCode {
  if (settingState === "disabled") return "unavailable";
  if (settingState === "blocked") return "needs_action";
  if (settingState === "pending") return "setup_incomplete";
  if (settingState === "unknown") return "needs_check";
  if (runtimeState === "available") return "available";
  if (runtimeState === "unavailable") return "unavailable";
  return "needs_check";
}

function DiagnosticValueItem({
  label,
  value,
  children,
}: {
  label: string;
  value?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2">
      <dt className="text-sm text-slate-600">{label}</dt>
      <dd className="text-right text-sm font-medium text-slate-950">
        {children ?? value}
      </dd>
    </div>
  );
}

type StatusEvidenceTone = "complete" | "current" | "upcoming" | "attention";

const STATUS_EVIDENCE_CLASSES: Record<StatusEvidenceTone, string> = {
  complete: "border-emerald-200 bg-emerald-50 text-emerald-900",
  current: "border-amber-200 bg-amber-50 text-amber-950",
  upcoming: "border-slate-200 bg-slate-50 text-slate-700",
  attention: "border-red-200 bg-red-50 text-red-900",
};

const CREDENTIAL_VERIFICATION_CLASSES: Record<CredentialVerificationTone, string> = {
  success: "border-emerald-200 bg-emerald-50 text-emerald-800",
  attention: "border-red-200 bg-red-50 text-red-800",
  neutral: "border-slate-200 bg-slate-50 text-slate-700",
};

function StatusEvidenceItem({
  index,
  label,
  value,
  description,
  tone,
}: {
  index: number;
  label: string;
  value: string;
  description: string;
  tone: StatusEvidenceTone;
}) {
  return (
    <li className="flex gap-3 rounded-xl border border-slate-200 bg-white p-4">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-xs font-semibold text-slate-700">
        {index}
      </span>
      <div>
        <p className="text-sm font-medium text-slate-700">{label}</p>
        <div className="mt-2 space-y-2">
          <span
            className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_EVIDENCE_CLASSES[tone]}`}
          >
            {value}
          </span>
          <p className="text-sm leading-6 text-slate-600">{description}</p>
        </div>
      </div>
    </li>
  );
}

function CredentialVerificationItem({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: ReactNode;
  tone?: CredentialVerificationTone;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <dt className="text-xs font-medium text-slate-500">{label}</dt>
      <dd className="mt-1">
        <span
          className={`inline-flex max-w-full rounded-full border px-2.5 py-0.5 text-sm font-medium ${CREDENTIAL_VERIFICATION_CLASSES[tone]}`}
        >
          <span className="truncate">{value}</span>
        </span>
      </dd>
    </div>
  );
}

function getTranslatedStateLabel(
  t: ReturnType<typeof useTranslation>["t"],
  path: string,
  key: string | null | undefined,
) {
  if (!key) {
    return null;
  }
  // Intentionally no defaultValue: missing i18n keys must surface visibly so
  // that new backend state values are caught in QA rather than silently
  // masked by a generic string.
  return t(`${path}.${key}`);
}

function getWarningCopy(
  t: ReturnType<typeof useTranslation>["t"],
  warning: string,
) {
  return {
    // Title intentionally has no defaultValue: an unknown warning code must
    // surface as the raw key so missing translations are caught in review.
    title: t(`detail.warningLabels.${warning}`),
    // Body falls back to the explicitly-designed `warningBodies.generic` entry
    // (a catch-all copy owned by design); this is a documented fallback, not
    // an error swallow.
    body: t(`detail.warningBodies.${warning}`, {
      defaultValue: t("detail.warningBodies.generic"),
    }),
  };
}

function isCredentialVerified(state: Pick<
  StorageCredentialStateResponse,
  "connectionState" | "credentialVerificationState"
>) {
  return (
    state.connectionState === "connected" &&
    state.credentialVerificationState === "verified"
  );
}

function readSmbRootFromLocation(storage: StorageDetailResponse) {
  if (storage.connectionKindId !== "smb") {
    return null;
  }
  const [host = "", shareName = "", ...basePathParts] =
    storage.storageLocationDisplay.pathParts;
  return {
    host,
    shareName,
    basePath: basePathParts.join("/"),
  };
}

function StorageDetailContent({ storageId }: { storageId: string }) {
  const handoffMemory = useLocalGatewayHandoffMemory();
  const { t } = useTranslation("storages");
  const navigate = useNavigate();
  const { storage, isLoading, isReadinessRefreshing, error, refresh } =
    useStorageDetail(storageId);
  const {
    mutate: issueLocalGatewaySetupHandoff,
    isLoading: isIssuingSetupHandoff,
    error: setupHandoffError,
  } = useIssueLocalGatewaySetupHandoff();
  const {
    mutate: issueLocalGatewayRouteHandoff,
    isLoading: isIssuingRouteHandoff,
    error: routeHandoffError,
  } = useIssueLocalGatewayRouteHandoff();
  const {
    mutate: deleteLocalGatewayRoute,
    isLoading: isDeletingRoute,
    error: deleteRouteMutationError,
    reset: resetDeleteRouteError,
  } = useDeleteLocalGatewayRoute(storageId);
  const {
    mutate: deleteAllLocalGatewayRoutes,
    isLoading: isDeletingAllRoutes,
    reset: resetDeleteAllRoutesError,
  } = useDeleteAllLocalGatewayRoutes(storageId);
  const {
    mutate: putStorageCredentialMaterial,
    isLoading: isSavingCredentialMaterial,
    error: credentialMaterialError,
    reset: resetCredentialMaterialError,
  } = usePutStorageCredentialMaterial(storageId);
  const {
    mutate: verifyStorageCredential,
    isLoading: isVerifyingCredential,
    error: credentialVerifyError,
    reset: resetCredentialVerifyError,
  } = useVerifyStorageCredential(storageId);
  const {
    mutate: resetStorageCredential,
    isLoading: isResettingCredential,
    error: credentialResetError,
    reset: resetCredentialResetError,
  } = useResetStorageCredential(storageId);
  const {
    mutate: updateSmbRoot,
    isLoading: isUpdatingSmbRoot,
    error: smbRootUpdateError,
    reset: resetSmbRootUpdateError,
  } = useUpdateSmbRoot(storageId);
  const {
    mutate: updateStorageDisplayName,
    isLoading: isUpdatingStorageDisplayName,
    error: storageDisplayNameUpdateError,
    reset: resetStorageDisplayNameUpdateError,
  } = useUpdateStorageDisplayName(storageId);
  const [setupHandoff, setSetupHandoff] =
    useState<LocalGatewayHandoffResponse | null>(() => handoffMemory.read(storageId)?.handoff ?? null);
  const [
    setupHandoffPollingOptions,
    setSetupHandoffPollingOptions,
  ] = useState<IssuedLocalGatewayHandoffPollingOptions | null>(() => handoffMemory.read(storageId)?.pollingOptions ?? null);
  const handleRegisteredHandoff = useCallback(
    (registeredStorageId: string, readiness: StorageReadinessResponse) => {
      if (registeredStorageId !== storageId) {
        return;
      }
      handoffMemory.forget(storageId);
      setSetupHandoff(null);
      setSetupHandoffPollingOptions(null);
      void refresh(readiness);
    },
    [refresh, storageId, handoffMemory],
  );
  const {
    pollingStatus: issuedHandoffPollingStatus,
    isExpired: setupHandoffExpired,
    error: setupHandoffPollingError,
    refreshRegistrationStatus,
    restartPolling: restartIssuedHandoffPolling,
  } = useIssuedLocalGatewayHandoffPolling(
    setupHandoff,
    handleRegisteredHandoff,
    { ...setupHandoffPollingOptions, onDiscardHandoff: handoffMemory.forget },
  );
  const {
    pollingStatus: localSetupPollingStatus,
    restartPolling: restartLocalSetupPolling,
  } = useLocalSetupPolling(
    storageId,
    storage,
    refresh,
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [installerOpen, setInstallerOpen] = useState<boolean | null>(null);
  const [gatewayAddOpen, setGatewayAddOpen] = useState(false);
  const [routeDeleteTarget, setRouteDeleteTarget] =
    useState<GatewayRoute | null>(null);
  const [showAllGatewayRoutesDelete, setShowAllGatewayRoutesDelete] =
    useState(false);
  const [routeDeleteError, setRouteDeleteError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<
    "registrationToken" | null
  >(null);
  const [credentialFormOpen, setCredentialFormOpen] = useState(false);
  const [displayNameFormOpen, setDisplayNameFormOpen] = useState(false);
  const [storageDisplayNameInput, setStorageDisplayNameInput] = useState("");
  const [storageDisplayNameError, setStorageDisplayNameError] =
    useState<string | null>(null);
  const [smbRootFormOpen, setSmbRootFormOpen] = useState(false);
  const [smbRootHost, setSmbRootHost] = useState("");
  const [smbRootShareName, setSmbRootShareName] = useState("");
  const [smbRootBasePath, setSmbRootBasePath] = useState("");
  const [showCredentialReset, setShowCredentialReset] = useState(false);
  const [smbUsername, setSmbUsername] = useState("");
  const [smbPassword, setSmbPassword] = useState("");
  const [smbDomain, setSmbDomain] = useState("");
  const [smbAuthenticationDraft, setSmbAuthenticationDraft] = useState<{
    storageId: string;
    authMethod: "ntlmv2" | "kerberos" | "kerberos_ntlmv2";
    realm: string;
    kdcHost: string;
    disableRequestIntegrity: boolean;
  } | null>(null);
  const savedSmbAuthentication = storage?.credentialSummary?.smbAuthentication;
  const smbAuthentication = smbAuthenticationDraft?.storageId === storageId
    ? smbAuthenticationDraft
    : {
        authMethod: savedSmbAuthentication?.authMethod ?? "ntlmv2",
        disableRequestIntegrity: savedSmbAuthentication && savedSmbAuthentication.authMethod !== "ntlmv2" ? savedSmbAuthentication.kerberos.disableRequestIntegrity ?? false : false,
        realm: (savedSmbAuthentication?.authMethod === "kerberos" || savedSmbAuthentication?.authMethod === "kerberos_ntlmv2") ? savedSmbAuthentication.kerberos.realm : "",
        kdcHost: (savedSmbAuthentication?.authMethod === "kerberos" || savedSmbAuthentication?.authMethod === "kerberos_ntlmv2") ? savedSmbAuthentication.kerberos.kdcHost : "",
      };
  const updateSmbAuthentication = (patch: Partial<typeof smbAuthentication>) => {
    setSmbAuthenticationDraft({ ...smbAuthentication, ...patch, storageId });
  };
  const [credentialActionFeedback, setCredentialActionFeedback] = useState<{
    message: string;
    tone: CredentialActionFeedbackTone;
  } | null>(null);

  if (isLoading && !storage) {
    return (
      <div className="p-6">
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-md bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  if (!storage) {
    // No silent fallback: if the hook reports no storage, it must also report
    // an error. Render whatever `error` carries so the real failure surfaces.
    return (
      <div className="flex h-64 items-center justify-center p-6 text-destructive">
        {error}
      </div>
    );
  }

  const refreshError = error;

  const handleConfirmRouteDelete = async () => {
    if (!routeDeleteTarget) {
      return;
    }
    setRouteDeleteError(null);
    resetDeleteRouteError();
    try {
      await deleteLocalGatewayRoute({
        routeId: routeDeleteTarget.routeId,
      });
      setRouteDeleteTarget(null);
      await refresh();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? t(`detail.gatewayRouteDeleteErrors.${err.code}`, {
              defaultValue: err.message,
            })
          : err instanceof Error
            ? err.message
            : t("detail.gatewayRouteDeleteErrors.generic");
      setRouteDeleteError(message);
      await refresh();
    }
  };

  const handleConfirmAllGatewayRoutesDelete = async () => {
    setRouteDeleteError(null);
    resetDeleteRouteError();
    try {
      await deleteAllLocalGatewayRoutes();
      setShowAllGatewayRoutesDelete(false);
      await refresh();
    } catch (err) {
      const message =
        err instanceof ApiError
          ? t(`detail.gatewayRouteDeleteErrors.${err.code}`, {
              defaultValue: err.message,
            })
          : err instanceof Error
            ? err.message
            : t("detail.gatewayRouteDeleteErrors.generic");
      setRouteDeleteError(message);
      await refresh();
    }
  };

  const handleOpenRouteDelete = (route: GatewayRoute) => {
    resetDeleteRouteError();
    setRouteDeleteError(null);
    setRouteDeleteTarget(route);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const handoffConsumed = setupHandoff
        ? await refreshRegistrationStatus()
        : false;
      if (!handoffConsumed) {
        await refresh();
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  const resolveCredentialActionFeedback = (
    state: StorageCredentialStateResponse,
    successMessage: string,
  ): { message: string; tone: CredentialActionFeedbackTone } => {
    if (isCredentialVerified(state)) {
      return { message: successMessage, tone: "success" };
    }
    if (state.credentialMaterialState === "configured" &&
        (state.lastFailureReasonCode === "route_not_allowed" ||
         state.lastFailureReasonCode === "relay_policy_denied")) {
      return {
        message: t("detail.credentialPanel.relayApprovalRequired"),
        tone: "attention",
      };
    }
    return {
      message: t("detail.credentialPanel.verificationNeedsAttention"),
      tone: "attention",
    };
  };

  const handleSubmitSmbCredential = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    resetCredentialMaterialError();
    resetCredentialVerifyError();
    setCredentialActionFeedback(null);
    try {
      const state = await putStorageCredentialMaterial({
        username: smbUsername.trim(),
        password: smbPassword,
        authMethod: smbAuthentication.authMethod,
        ...(smbAuthentication.authMethod !== "ntlmv2"
          ? { kerberos: {
              realm: smbAuthentication.realm.trim().toUpperCase(),
              kdcHost: smbAuthentication.kdcHost.trim().toLowerCase(),
              ...(smbAuthentication.disableRequestIntegrity ? { disableRequestIntegrity: true } : {}),
            }, ...(smbAuthentication.authMethod === "kerberos_ntlmv2" ? { domain: smbDomain } : {}) }
          : { domain: smbDomain }),
      });
      if (state.credentialMaterialState === "configured") {
        setSmbUsername("");
        setSmbPassword("");
        setSmbDomain("");
      }
      if (isCredentialVerified(state)) {
        setCredentialFormOpen(false);
      }
      setCredentialActionFeedback(
        resolveCredentialActionFeedback(
          state,
          t("detail.credentialPanel.saved"),
        ),
      );
      await refresh();
    } catch {
      // The hook owns the customer-facing error state.
    }
  };

  const handleVerifySmbCredential = async () => {
    resetCredentialMaterialError();
    resetCredentialVerifyError();
    resetCredentialResetError();
    setCredentialActionFeedback(null);
    try {
      const state = await verifyStorageCredential();
      setCredentialActionFeedback(
        resolveCredentialActionFeedback(
          state,
          t("detail.credentialPanel.verified"),
        ),
      );
      await refresh();
    } catch {
      // The hook owns the customer-facing error state.
    }
  };

  const handleResetSmbCredential = async () => {
    resetCredentialMaterialError();
    resetCredentialVerifyError();
    resetCredentialResetError();
    setCredentialActionFeedback(null);
    try {
      await resetStorageCredential();
      setShowCredentialReset(false);
      setSmbAuthenticationDraft(null);
      setSmbUsername("");
      setSmbPassword("");
      setSmbDomain("");
      setCredentialFormOpen(true);
      setCredentialActionFeedback({
        message: t("detail.credentialPanel.deleted"),
        tone: "attention",
      });
      await refresh();
    } catch {
      // The hook owns the customer-facing error state.
    }
  };

  const handleOpenSmbRootForm = () => {
    const smbRoot = readSmbRootFromLocation(storage);
    setSmbRootHost(smbRoot?.host ?? "");
    setSmbRootShareName(smbRoot?.shareName ?? "");
    setSmbRootBasePath(smbRoot?.basePath ?? "");
    resetSmbRootUpdateError();
    setSmbRootFormOpen(true);
  };

  const handleCancelSmbRootForm = () => {
    resetSmbRootUpdateError();
    setSmbRootFormOpen(false);
  };

  const handleOpenDisplayNameForm = () => {
    resetStorageDisplayNameUpdateError();
    setStorageDisplayNameError(null);
    setStorageDisplayNameInput(storage.displayName);
    setDisplayNameFormOpen(true);
  };

  const handleCancelDisplayNameForm = () => {
    resetStorageDisplayNameUpdateError();
    setStorageDisplayNameError(null);
    setDisplayNameFormOpen(false);
  };

  const handleSubmitStorageDisplayName = async (
    event: FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    resetStorageDisplayNameUpdateError();
    setStorageDisplayNameError(null);
    const storageDisplayName = storageDisplayNameInput.trim();
    if (!storageDisplayName) {
      setStorageDisplayNameError(t("detail.displayNameRequired"));
      return;
    }
    if (storageDisplayName.length > STORAGE_DISPLAY_NAME_MAX_LENGTH) {
      setStorageDisplayNameError(t("detail.displayNameTooLong"));
      return;
    }
    try {
      const updated = await updateStorageDisplayName({ storageDisplayName });
      setDisplayNameFormOpen(false);
      const remembered = handoffMemory.read(storageId);
      if (remembered && remembered.handoff.registrationToken === setupHandoff?.registrationToken) {
        handoffMemory.remember(
          { ...remembered.handoff, storageDisplayName: updated.displayName },
          remembered.pollingOptions,
        );
      }
      setSetupHandoff((current) =>
        current
          ? { ...current, storageDisplayName: updated.displayName }
          : current,
      );
      await refresh();
    } catch {
      // The hook owns the customer-facing error state.
    }
  };

  const handleSubmitSmbRoot = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    resetSmbRootUpdateError();
    try {
      await updateSmbRoot({
        host: smbRootHost,
        shareName: smbRootShareName,
        basePath: normalizeSmbBasePathInput(smbRootBasePath),
      });
      setSmbRootFormOpen(false);
      handoffMemory.forget(storageId);
      setSetupHandoff(null);
      setSetupHandoffPollingOptions(null);
      await refresh();
    } catch {
      // The hook owns the customer-facing error state.
    }
  };

  const handleIssueSetupHandoff = async () => {
    const routeLimit = getLocalGatewayRouteLimit(storage);
    if (
      routeLimit !== null &&
      (storage.gatewayRouteSummary?.activeRouteCount ?? 0) >= routeLimit
    ) {
      return;
    }
    const handoffPurpose =
      storage.connectionKindId === "smb"
        ? "gateway_route_attach"
        : "local_setup";
    const initialRouteIds = (storage.gatewayRouteSummary?.routes ?? [])
      .filter((route) => route.lifecycle === "active")
      .map((route) => route.routeId);
    const pollingOptions: IssuedLocalGatewayHandoffPollingOptions = {
      purpose: handoffPurpose,
      initialActiveRouteCount:
        storage.gatewayRouteSummary?.activeRouteCount ?? 0,
      initialRouteIds,
    };
    const result =
      storage.connectionKindId === "smb"
        ? await issueLocalGatewayRouteHandoff({
            storageId,
          })
        : await issueLocalGatewaySetupHandoff({
            connectionKindId: "local_folder",
            storageId,
            displayName: storage.displayName,
          });
    handoffMemory.remember(result, pollingOptions);
    setSetupHandoffPollingOptions(pollingOptions);
    setSetupHandoff(result);
    setCopiedField(null);
    void refresh();
  };

  const handleCopyRegistrationToken = async (
    event?: MouseEvent<HTMLButtonElement>,
  ) => {
    event?.preventDefault();
    event?.stopPropagation();
    if (!setupHandoff) {
      return;
    }
    await copyTextToClipboard(setupHandoff.registrationToken);
    setCopiedField("registrationToken");
  };

  const isLocalStorage = isLocalCapableStorage(storage);
  const actionCode = resolveStorageActionCode(storage);
  const needsCredentialInput = actionCode === "repair_credentials" &&
    storage.credentialSummary?.credentialMaterialState === "not_configured";
  const currentStatusBodyKey = needsCredentialInput
    ? "detail.progressDescriptions.credential_not_configured"
    : storage.connectionKindId === "smb" && actionCode === "wait_auto_publication"
      ? "detail.actionBodies.smb_wait_auto_publication"
      : `detail.actionBodies.${actionCode}`;
  const currentStatusBody = t(currentStatusBodyKey);
  const runtimeStatusSnapshot = storage.runtimeStatusSnapshot;
  const runtimeStatusLineCode = resolveRuntimeStatusLineCode(
    runtimeStatusSnapshot,
  );
  const storageSettingState = resolveStorageSettingState(storage);
  const availabilityCode = resolveStorageAvailabilityCode(
    storageSettingState,
    runtimeStatusLineCode,
  );
  const requestLocalGatewayRouteSummary = storage.gatewayRouteSummary;
  const requestLocalCurrentGatewayVersion = storage.currentGatewayVersion;
  const activeGatewayRouteCount =
    requestLocalGatewayRouteSummary?.activeRouteCount ?? 0;
  const gatewayRouteRows = requestLocalGatewayRouteSummary?.routes ?? [];
  const gatewayRouteMaxCount = getLocalGatewayRouteLimit(storage);
  const gatewayRouteLimitReached =
    gatewayRouteMaxCount !== null &&
    activeGatewayRouteCount >= gatewayRouteMaxCount;
  const hasValidZeroGatewayRouteEvidence =
    requestLocalGatewayRouteSummary !== null &&
    requestLocalGatewayRouteSummary !== undefined &&
    activeGatewayRouteCount === 0 &&
    requestLocalGatewayRouteSummary.availableRouteCount === 0 &&
    requestLocalGatewayRouteSummary.attentionRouteCount === 0 &&
    requestLocalGatewayRouteSummary.lastRouteAttachedAt === null &&
    gatewayRouteRows.length === 0;
  const localGatewayRouteMissing =
    isLocalStorage && hasValidZeroGatewayRouteEvidence;
  const hasPartialGatewayRouteAttention =
    storage.settingState === "ready" &&
    (requestLocalGatewayRouteSummary?.availableRouteCount ?? 0) > 0 &&
    (requestLocalGatewayRouteSummary?.attentionRouteCount ?? 0) > 0;
  const shouldShowStorageDisplayLocation =
    storage.storageLocationDisplay.displayName !== storage.displayName;
  const shouldOfferPlatformMappingRepairHandoff =
    (storage.settingBlockedReason === "platform_response_invalid" ||
      storage.settingBlockedReason === "storage_deleted") &&
    (storage.requiredRecoveryAction === "gateway_route_repair" ||
      storage.requiredRecoveryAction === "registration_rerun") &&
    requestLocalGatewayRouteSummary == null;
  const shouldOfferPendingLocalFolderHandoff =
    storage.connectionKindId === "local_folder" &&
    storage.storageLifecycleState === "active" &&
    storage.settingState === "pending" &&
    storage.settingBlockedReason === "registration_incomplete" &&
    storage.requiredRecoveryAction === "registration_rerun" &&
    requestLocalGatewayRouteSummary === null;
  const shouldOfferManualPublicationHandoff =
    storage.connectionKindId === "local_folder" &&
    storage.settingBlockedReason === "publication_not_ready" &&
    storage.requiredRecoveryAction === "gateway_route_repair" &&
    storage.publicationSummary?.desiredPublicationMode === "manual" &&
    (requestLocalGatewayRouteSummary == null || hasValidZeroGatewayRouteEvidence);
  const shouldOfferHandoff =
    isLocalStorage &&
    (localGatewayRouteMissing ||
      shouldOfferPendingLocalFolderHandoff ||
      shouldOfferPlatformMappingRepairHandoff ||
      shouldOfferManualPublicationHandoff);
  const hasGatewayRouteDeleteSelector =
    isLocalStorage && gatewayRouteRows.length > 0;
  const shouldOfferAllGatewayRoutesDelete =
    isLocalStorage &&
    !shouldOfferHandoff &&
    !localGatewayRouteMissing &&
    !hasGatewayRouteDeleteSelector &&
    (requestLocalGatewayRouteSummary == null ||
      activeGatewayRouteCount > 0 ||
      storage.settingBlockedReason === "platform_response_invalid" ||
      storage.requiredRecoveryAction === "gateway_route_repair");
  const operabilityWarnings = storage.operabilityWarnings ?? [];
  const warningCopies = operabilityWarnings.map((warning) => ({
    key: warning,
    ...getWarningCopy(t, warning),
  }));
  const normalizedLocalGatewaySetupState =
    storage.settingState === "ready" &&
    [
      "setup_pending",
      "owner_auth_pending",
      "registration_pending",
      "binding_in_progress",
    ].includes(storage.localGatewaySetupState)
      ? "completed"
      : storage.settingState === "ready" &&
          storage.localGatewaySetupState === "publication_pending"
        ? "publication_rechecking"
        : storage.localGatewaySetupState;
  const localGatewaySetupStateLabel = getTranslatedStateLabel(
    t,
    "detail.localGatewaySetupStates",
    normalizedLocalGatewaySetupState,
  );
  const smbRoot = readSmbRootFromLocation(storage);
  const isSmbRootHostChanged =
    smbRootFormOpen && smbRoot !== null && smbRootHost.trim() !== smbRoot.host;
  const credentialStateLabel = getTranslatedStateLabel(
    t,
    "detail.credentialStates",
    storage.credentialSummary?.credentialMaterialState === "not_configured"
      ? "not_configured"
      : storage.credentialSummary?.credentialVerificationState ??
        storage.credentialSummary?.connectionState,
  );
  const desiredPublicationModeLabel = getTranslatedStateLabel(
    t,
    "detail.publicationModes",
    storage.publicationSummary?.desiredPublicationMode,
  );
  const effectivePublicationModeLabel = getTranslatedStateLabel(
    t,
    "detail.publicationModes",
    storage.publicationSummary?.effectivePublicationMode,
  );
  const shouldShowPublicationTechnicalDetails =
    actionCode === "manual_publication" ||
    actionCode === "wait_auto_publication" ||
    actionCode === "wait_route" ||
    storage.settingBlockedReason === "publication_not_ready" ||
    Boolean(storage.publicationSummary?.effectivePublishedOrigin);
  const shouldShowGatewayVersionTechnicalDetails =
    storage.settingBlockedReason === "gateway_version_incompatible" ||
    Boolean(requestLocalCurrentGatewayVersion);
  const shouldShowTechnicalDetails =
    shouldShowPublicationTechnicalDetails ||
    shouldShowGatewayVersionTechnicalDetails;
  const credentialSummary = storage.credentialSummary;
  const isSmbScopedRuntimeCredential =
    storage.connectionKindId === "smb" &&
    credentialSummary?.credentialKindSummary === "scoped_runtime";
  const progressSteps = isLocalStorage
    ? resolveLocalStorageProgressSteps(storage)
    : [];
  const progressStepById = new Map(
    progressSteps.map((step) => [step.id, step] as const),
  );
  const localGatewayProgressStep = progressStepById.get("gateway");
  const localRootProgressStep = progressStepById.get("root");
  const publicationProgressStep = progressStepById.get("publication");
  const resolveEvidenceTone = (
    step: typeof localGatewayProgressStep,
  ): StatusEvidenceTone => {
    if (!step) return "upcoming";
    if (step.state === "complete") return "complete";
    if (step.descriptionCode === "review_status") return "attention";
    return step.state === "current" ? "current" : "upcoming";
  };
  const resolvePublicationEvidenceValue = () => {
    if (!publicationProgressStep || publicationProgressStep.state === "upcoming") {
      return t("detail.statusEvidenceValues.notStarted");
    }
    if (publicationProgressStep.state === "complete") {
      return t("detail.statusEvidenceValues.routeEstablished");
    }
    switch (publicationProgressStep.descriptionCode) {
      case "manual_publication":
        return t("detail.statusEvidenceValues.pcPublicationWaiting");
      case "wait_auto_publication":
        return t("detail.statusEvidenceValues.autoReflecting");
      case "wait_route":
        return t("detail.statusEvidenceValues.routeReflecting");
      case "review_status":
        return t("detail.statusEvidenceValues.needsCheck");
      default:
        return t("detail.statusEvidenceValues.inProgress");
    }
  };
  const resolvePublicationEvidenceDescription = () => {
    if (!publicationProgressStep) {
      return t("detail.progressDescriptions.pending_ready");
    }
    if (storage.connectionKindId === "smb") {
      if (publicationProgressStep.state === "complete") {
        return t("detail.progressDescriptions.smb_publication_route_ready");
      }
      if (publicationProgressStep.descriptionCode === "manual_publication") {
        return t("detail.progressDescriptions.manual_publication");
      }
      if (publicationProgressStep.descriptionCode === "wait_route") {
        return t("detail.progressDescriptions.smb_publication_route_pending");
      }
      if (publicationProgressStep.descriptionCode === "wait_auto_publication") {
        return t("detail.progressDescriptions.wait_auto_publication");
      }
      return t("detail.progressDescriptions.smb_publication_route_review");
    }
    if (publicationProgressStep.state === "complete") {
      return t("detail.progressDescriptions.publication_ready");
    }
    return t(
      `detail.progressDescriptions.${publicationProgressStep.descriptionCode}`,
    );
  };
  const resolveCredentialEvidence = (): {
    value: string;
    description: string;
    tone: StatusEvidenceTone;
  } | null => {
    if (!credentialSummary) {
      return null;
    }
    if (
      credentialSummary.connectionState === "connected" &&
      credentialSummary.credentialVerificationState === "verified"
    ) {
      return {
        value: t("detail.statusEvidenceValues.credentialVerified"),
        description: t("detail.progressDescriptions.credential_verified"),
        tone: "complete",
      };
    }
    if (
      credentialSummary.connectionState === "credential_invalid" ||
      credentialSummary.credentialVerificationState === "failed"
    ) {
      return {
        value: t("detail.statusEvidenceValues.needsCheck"),
        description: t("detail.progressDescriptions.credential_failed"),
        tone: "attention",
      };
    }
    if (credentialSummary.credentialMaterialState === "configured") {
      return {
        value: t("detail.statusEvidenceValues.credentialRecheckWaiting"),
        description: t("detail.progressDescriptions.credential_recheck_waiting"),
        tone: "current",
      };
    }
    return {
      value: t("detail.statusEvidenceValues.credentialInputWaiting"),
      description: t("detail.progressDescriptions.credential_not_configured"),
      tone: "current",
    };
  };
  const credentialEvidence = resolveCredentialEvidence();
  const smbProtocolProbeNotObservedAfterRecheck =
    storage.smbProtocolReachabilitySummary?.state === "not_observed" &&
    storage.smbProtocolReachabilitySummary.lastFailureReasonCode ===
      "smb_protocol_probe_not_observed" &&
    publicationProgressStep?.state === "complete";
  const progressValue = (
    step: typeof localGatewayProgressStep,
    values: { complete: string; current: string },
  ) => {
    if (!step) return t("detail.statusEvidenceValues.notStarted");
    if (step.state === "complete") {
      return values.complete;
    }
    if (step.state === "current") {
      return values.current;
    }
    return t("detail.statusEvidenceValues.notStarted");
  };
  const currentStateUnavailable =
    storage.operabilityWarnings?.includes("platform_sync_failed") ||
    storage.localGatewaySetupState === "not_observed";
  const smbProtocolEvidence = localRootProgressStep
    ? {
        key: "smb-protocol",
        label: t("detail.statusEvidenceLabels.smbProtocol"),
        value:
          storage.smbProtocolReachabilitySummary?.state === "reachable"
            ? t("detail.statusEvidenceValues.protocolReachable")
            : storage.smbProtocolReachabilitySummary?.state === "unreachable"
              ? t("detail.statusEvidenceValues.needsCheck")
              : smbProtocolProbeNotObservedAfterRecheck
                ? t("detail.statusEvidenceValues.needsCheck")
              : publicationProgressStep?.state === "complete"
                ? t("detail.statusEvidenceValues.recheckWaiting")
                : t("detail.statusEvidenceValues.notStarted"),
        description: t(
          storage.smbProtocolReachabilitySummary?.state === "reachable"
            ? "detail.progressDescriptions.smb_protocol_reachable"
            : storage.smbProtocolReachabilitySummary?.state === "unreachable"
              ? storage.smbProtocolReachabilitySummary.lastFailureReasonCode ===
                "smb_protocol_timeout"
                ? "detail.progressDescriptions.smb_protocol_timeout"
                : "detail.progressDescriptions.smb_protocol_unreachable"
              : smbProtocolProbeNotObservedAfterRecheck
                ? "detail.progressDescriptions.smb_protocol_probe_not_observed_after_recheck"
              : publicationProgressStep?.state === "complete"
                ? "detail.progressDescriptions.smb_protocol_not_observed_after_publication"
                : "detail.progressDescriptions.smb_protocol_wait_publication",
        ),
        tone:
          storage.smbProtocolReachabilitySummary?.state === "reachable"
            ? ("complete" as const)
            : storage.smbProtocolReachabilitySummary?.state === "unreachable"
              ? ("attention" as const)
              : smbProtocolProbeNotObservedAfterRecheck
                ? ("attention" as const)
              : publicationProgressStep?.state === "complete"
                ? ("current" as const)
                : ("upcoming" as const),
      }
    : null;
  const rawStatusEvidenceItems = !isLocalStorage || currentStateUnavailable
    ? []
    : storage.connectionKindId === "smb"
      ? [
          localGatewayProgressStep
            ? {
                key: "gateway",
                label: t("detail.statusEvidenceLabels.localGateway"),
                value:
                  localGatewayProgressStep.state === "complete"
                    ? t("detail.statusEvidenceValues.pcLinked")
                    : localGatewayProgressStep.state === "current"
                      ? t("detail.statusEvidenceValues.pcRegistrationWaiting")
                      : t("detail.statusEvidenceValues.notStarted"),
                description: t(
                  `detail.progressDescriptions.${localGatewayProgressStep.descriptionCode}`,
                ),
                tone: resolveEvidenceTone(localGatewayProgressStep),
              }
            : null,
          localRootProgressStep
            ? {
                key: "smb-relay",
                label: t("detail.statusEvidenceLabels.smbRelay"),
                value: progressValue(localRootProgressStep, {
                  complete: t("detail.statusEvidenceValues.relayApproved"),
                  current: t("detail.statusEvidenceValues.relayApprovalWaiting"),
                }),
                description: t(
                  localRootProgressStep.state === "complete"
                    ? "detail.progressDescriptions.smb_relay_confirmed"
                    : "detail.progressDescriptions.smb_relay_pending",
                ),
                tone: resolveEvidenceTone(localRootProgressStep),
              }
            : null,
          publicationProgressStep
            ? {
                key: "publication",
                label: t("detail.statusEvidenceLabels.smbPublicationRoute"),
                value: resolvePublicationEvidenceValue(),
                description: resolvePublicationEvidenceDescription(),
                tone: resolveEvidenceTone(publicationProgressStep),
              }
            : null,
          smbProtocolEvidence,
          isSmbScopedRuntimeCredential && credentialEvidence
            ? {
                key: "credential",
                label: t("detail.statusEvidenceLabels.credential"),
                value: credentialEvidence.value,
                description: credentialEvidence.description,
                tone: credentialEvidence.tone,
              }
            : null,
        ]
      : [
          localGatewayProgressStep
            ? {
                key: "gateway",
                label: t("detail.statusEvidenceLabels.localGateway"),
                value:
                  localGatewayProgressStep.state === "complete"
                    ? t("detail.statusEvidenceValues.pcLinked")
                    : localGatewayProgressStep.state === "current"
                      ? t("detail.statusEvidenceValues.pcRegistrationWaiting")
                      : t("detail.statusEvidenceValues.notStarted"),
                description: t(
                  `detail.progressDescriptions.${localGatewayProgressStep.descriptionCode}`,
                ),
                tone: resolveEvidenceTone(localGatewayProgressStep),
              }
            : null,
          localRootProgressStep
            ? {
                key: "root",
                label: t("detail.statusEvidenceLabels.localRoot"),
                value: progressValue(localRootProgressStep, {
                  complete: t("detail.statusEvidenceValues.folderSelected"),
                  current: t("detail.statusEvidenceValues.folderSelectionWaiting"),
                }),
                description: t(
                  `detail.progressDescriptions.${localRootProgressStep.descriptionCode}`,
                ),
                tone: resolveEvidenceTone(localRootProgressStep),
              }
            : null,
          publicationProgressStep
            ? {
                key: "publication",
                label: t("detail.statusEvidenceLabels.publication"),
                value: resolvePublicationEvidenceValue(),
                description: resolvePublicationEvidenceDescription(),
                tone: resolveEvidenceTone(publicationProgressStep),
              }
            : null,
        ];
  const statusEvidenceItems = rawStatusEvidenceItems.filter((item): item is {
    key: string;
    label: string;
    value: string;
    description: string;
    tone: StatusEvidenceTone;
  } => item !== null);
  const isIssuingHandoff = isIssuingSetupHandoff || isIssuingRouteHandoff;
  const handoffError = routeHandoffError ?? setupHandoffError;
  const effectivePollingStatus = setupHandoff
    ? issuedHandoffPollingStatus
    : localSetupPollingStatus;
  const restartEffectivePolling = setupHandoff
    ? restartIssuedHandoffPolling
    : restartLocalSetupPolling;
  const isManualRefreshBusy = isRefreshing;
  const canIssueHandoffNow = setupHandoff === null || setupHandoffExpired;
  const canIssueGatewayRouteHandoff =
    canIssueHandoffNow && !gatewayRouteLimitReached;
  const shouldShowRecoveryGuidance =
    storage.settingState !== "ready" ||
    shouldOfferHandoff ||
    shouldOfferAllGatewayRoutesDelete;
  const shouldShowDay2Handoff =
    storage.connectionKindId === "smb" &&
    !shouldOfferHandoff &&
    !shouldShowRecoveryGuidance &&
    !gatewayRouteLimitReached;
  const shouldShowRecoveryInstaller = shouldShowRecoveryGuidance && shouldOfferHandoff;
  const isInstallerOpen = installerOpen ?? shouldShowRecoveryInstaller;
  const shouldShowStandalonePollingIndicator =
    shouldShowRecoveryGuidance &&
    effectivePollingStatus === "polling" &&
    setupHandoff === null;
  const shouldShowSmbCredentialForm =
    isSmbScopedRuntimeCredential &&
    (credentialFormOpen ||
      credentialSummary?.credentialVerificationState !== "verified");
  const canResetCredential =
    isSmbScopedRuntimeCredential &&
    credentialSummary?.credentialMaterialState === "configured";
  const credentialMutationError =
    credentialMaterialError ?? credentialVerifyError ?? credentialResetError;
  const credentialBusy =
    isSavingCredentialMaterial || isVerifyingCredential || isResettingCredential;
  const shouldRenderInlineCredentialForm =
    isSmbScopedRuntimeCredential && actionCode === "repair_credentials";
  const credentialVerification = (() => {
    switch (credentialSummary?.credentialVerificationState) {
      case "verified":
        return {
          label: t("detail.credentialPanel.verificationResults.success"),
          tone: "success" as const,
        };
      case "failed":
        return {
          label: t("detail.credentialPanel.verificationResults.failure"),
          tone: "attention" as const,
        };
      case "not_verified":
        return {
          label: t("detail.credentialPanel.verificationResults.pending"),
          tone: "neutral" as const,
        };
      default:
        if (credentialSummary?.credentialMaterialState === "not_configured") {
          return {
            label: t("detail.credentialPanel.verificationResults.notConfigured"),
            tone: "neutral" as const,
          };
        }
        return {
          label: t("detail.credentialPanel.verificationResults.notRegistered"),
          tone: "neutral" as const,
        };
    }
  })();
  const credentialFailureReasonLabel =
    credentialSummary?.lastFailureReasonCode
      ? t(
          `detail.credentialPanel.failureReasons.${credentialSummary.lastFailureReasonCode}`,
        )
      : null;
  const credentialLastVerifiedAt = credentialSummary?.lastVerifiedAt
    ? new Date(credentialSummary.lastVerifiedAt).toLocaleString("ja-JP")
    : "-";
  const credentialDeleteButtonClassName =
    "border-destructive/40 text-destructive shadow-none hover:border-destructive/60 hover:bg-destructive/10 hover:text-destructive";
  const smbCredentialFormElement = (
    <form
      className="grid gap-4 md:grid-cols-2"
      autoComplete="off"
      onSubmit={(event) => void handleSubmitSmbCredential(event)}
    >
      <p className="text-sm text-muted-foreground md:col-span-2">
        {t(smbAuthentication.authMethod !== "ntlmv2"
          ? "detail.credentialPanel.kerberosHelp"
          : "detail.credentialPanel.ntlmHelp")}
      </p>
      <div className="space-y-2 md:col-span-2">
        <Label htmlFor="smb-credential-auth-method">{t("detail.credentialPanel.authMethod")}</Label>
        <select id="smb-credential-auth-method"
          className="block w-full rounded-md border border-input bg-background p-2"
          value={smbAuthentication.authMethod} disabled={credentialBusy}
          onChange={(event) => updateSmbAuthentication({ authMethod: event.target.value as "ntlmv2" | "kerberos" | "kerberos_ntlmv2" })}>
          <option value="ntlmv2">{t("detail.credentialPanel.ntlmOnly")}</option>
          <option value="kerberos">{t("detail.credentialPanel.kerberosOnly")}</option>
          <option value="kerberos_ntlmv2">{t("detail.credentialPanel.kerberosPreferred")}</option>
        </select>
      </div>
      {smbAuthentication.authMethod !== "ntlmv2" ? <>
        <div className="space-y-2">
          <Label htmlFor="smb-credential-realm">{t("detail.credentialPanel.realm")}</Label>
          <Input id="smb-credential-realm" placeholder={t("detail.credentialPanel.realmPlaceholder")} autoComplete="off" required
            value={smbAuthentication.realm} disabled={credentialBusy}
            onChange={(event) => updateSmbAuthentication({ realm: event.target.value })} />
          <p className="text-sm text-muted-foreground">{t("detail.credentialPanel.realmHelp")}</p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="smb-credential-kdc">{t("detail.credentialPanel.kdcHost")}</Label>
          <Input id="smb-credential-kdc" placeholder={t("detail.credentialPanel.kdcPlaceholder")} autoComplete="off" required
            value={smbAuthentication.kdcHost} disabled={credentialBusy}
            onChange={(event) => updateSmbAuthentication({ kdcHost: event.target.value })} />
          <p className="text-sm text-muted-foreground">{t("detail.credentialPanel.kdcHelp")}</p>
        </div>
      </> : null}
      <div className="space-y-2">
        <Label htmlFor="smb-credential-username">
          {t("detail.credentialPanel.username")}
        </Label>
        <Input
          id="smb-credential-username" placeholder={t("detail.credentialPanel.usernamePlaceholder")}
          autoComplete="off"
          value={smbUsername}
          onChange={(event) => setSmbUsername(event.target.value)}
          disabled={credentialBusy}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="smb-credential-password">
          {t("detail.credentialPanel.password")}
        </Label>
        <Input
          id="smb-credential-password" placeholder={t("detail.credentialPanel.passwordPlaceholder")}
          type="password"
          autoComplete="new-password"
          value={smbPassword}
          onChange={(event) => setSmbPassword(event.target.value)}
          disabled={credentialBusy}
        />
      </div>
      {smbAuthentication.authMethod !== "kerberos" ? <div className="space-y-2 md:col-span-2">
        <Label htmlFor="smb-credential-domain">
          {t("detail.credentialPanel.domain")}
        </Label>
        <Input
          id="smb-credential-domain" placeholder={t("detail.credentialPanel.domainPlaceholder")}
          autoComplete="off"
          value={smbDomain}
          onChange={(event) => setSmbDomain(event.target.value)}
          disabled={credentialBusy}
        />
      </div> : null}
      {smbAuthentication.authMethod === "kerberos_ntlmv2" ? <p className="text-sm text-muted-foreground md:col-span-2">{t("detail.credentialPanel.fallbackHelp")}</p> : null}
      {smbAuthentication.authMethod !== "ntlmv2" ? <details className="md:col-span-2">
        <summary>{t("detail.credentialPanel.advanced")}</summary>
        <label className="flex gap-2 items-center"><input type="checkbox" disabled={credentialBusy}
          checked={smbAuthentication.disableRequestIntegrity}
          onChange={event => updateSmbAuthentication({ disableRequestIntegrity: event.target.checked })} />
          {t("detail.credentialPanel.disableIntegrity")}</label>
        <p className="text-sm text-muted-foreground">{t("detail.credentialPanel.integrityHelp")}</p>
      </details> : null}
      {smbAuthentication.authMethod !== "kerberos" ? <p className="text-sm text-muted-foreground md:col-span-2">{t("detail.credentialPanel.domainHelp")}</p> : null}
      <div className="flex flex-wrap gap-3 md:col-span-2">
        <Button
          type="submit"
          disabled={credentialBusy || !smbUsername.trim() || !smbPassword ||
            (smbAuthentication.authMethod !== "ntlmv2" &&
              (!smbAuthentication.realm.trim() || !smbAuthentication.kdcHost.trim()))}
        >
          {isSavingCredentialMaterial ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <KeyRound className="mr-2 h-4 w-4" />
          )}
          {t("detail.credentialPanel.saveAndVerify")}
        </Button>
      </div>
    </form>
  );
  const credentialActionFeedbackElement = (
    <>
      {credentialActionFeedback ? (
        <p
          className={`text-sm font-medium ${
            credentialActionFeedback.tone === "success"
              ? "text-emerald-700"
              : "text-amber-700"
          }`}
        >
          {credentialActionFeedback.message}
        </p>
      ) : null}
      {credentialMutationError ? (
        <p className="text-sm font-medium text-destructive">
          {credentialMutationError}
        </p>
      ) : null}
    </>
  );
  const inlineCredentialEditor = shouldRenderInlineCredentialForm ? (
    <div className="space-y-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-medium text-slate-950">
          {t("detail.credentialPanel.writeOnlyTitle")}
        </p>
        {canResetCredential ? (
          <Button
            type="button"
            variant="outline"
            className={credentialDeleteButtonClassName}
            onClick={() => setShowCredentialReset(true)}
            disabled={credentialBusy}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {t("detail.credentialPanel.deleteCredential")}
          </Button>
        ) : null}
      </div>
      {smbCredentialFormElement}
      <Button type="button" variant="outline" onClick={() => void handleVerifySmbCredential()}
        disabled={credentialBusy || !canResetCredential}>
        {isVerifyingCredential ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
        {t("detail.credentialPanel.verify")}
      </Button>
      {credentialActionFeedbackElement}
    </div>
  ) : null;
  const installerDownloadSection = isLocalStorage ? (
    <section>
      <Button type="button" variant="link" className="h-auto px-0 text-muted-foreground"
        aria-expanded={isInstallerOpen} aria-controls="gateway-installer-panel"
        onClick={() => setInstallerOpen(!isInstallerOpen)}>
        {isInstallerOpen ? <ChevronDown className="mr-2 h-4 w-4" aria-hidden="true" /> : <ChevronRight className="mr-2 h-4 w-4" aria-hidden="true" />}
        {t("detail.installerDownloadToggle")}
      </Button>
      {isInstallerOpen ? <div id="gateway-installer-panel" className="pt-3">
        <LocalGatewayInstallerDownload key={storage.storageId} />
      </div> : null}
    </section>
  ) : null;
  const handoffResultPanel = setupHandoff ? (
    <div className="rounded-2xl border border-sky-200 bg-sky-50/70 p-4">
      <div className="space-y-1">
        <p className="font-medium text-slate-950">
          {t("detail.handoffTitle")}
        </p>
      </div>
      <ol className="mt-4 space-y-2 text-sm text-slate-700">
        {(
          t(storage.connectionKindId === "smb" ? "detail.smbHandoffSteps" : "detail.handoffSteps", {
            returnObjects: true,
          }) as unknown as string[]
        ).map((step) => (
          <li key={step} className="flex gap-2">
            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-sky-600" />
            <span>{step}</span>
          </li>
        ))}
      </ol>
      <GatewayRegistrationCode
        registrationToken={setupHandoff.registrationToken}
        expiresAt={setupHandoff.expiresAt}
        copied={copiedField === "registrationToken"}
        onCopy={(event) => void handleCopyRegistrationToken(event)}
      />
      {setupHandoffExpired ? (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/80 px-3 py-2 text-sm text-amber-900">
          {t("detail.handoffExpired")}
        </div>
      ) : issuedHandoffPollingStatus === "polling" ? (
        <div className="mt-4 flex items-center gap-2 rounded-xl border border-sky-200 bg-white/70 px-3 py-2 text-sm text-sky-800">
          <Loader2 className="h-4 w-4 animate-spin" />
          {t("detail.pollingActive")}
        </div>
      ) : null}
      {setupHandoffPollingError ? (
        <div className="mt-4 rounded-xl border border-amber-200 bg-white/70 px-3 py-2 text-sm text-amber-900">
          <p>{t("detail.handoffPollingError")}</p>
          <p className="mt-1 text-xs text-amber-800">
            {setupHandoffPollingError}
          </p>
        </div>
      ) : null}
    </div>
  ) : null;

  const smbRootPanel = smbRoot ? (
    <ExpandablePanel title={t("detail.smbRoot.title")} defaultOpen>
      <div className="space-y-4">
        {smbRootFormOpen ? (
          <form className="space-y-4" onSubmit={handleSubmitSmbRoot}>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="smb-root-host">{t("detail.smbRoot.host")}</Label>
                <Input
                  id="smb-root-host"
                  value={smbRootHost}
                  onChange={(event) => setSmbRootHost(event.target.value)}
                  disabled={isUpdatingSmbRoot}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smb-root-share">
                  {t("detail.smbRoot.shareName")}
                </Label>
                <Input
                  id="smb-root-share"
                  value={smbRootShareName}
                  onChange={(event) => setSmbRootShareName(event.target.value)}
                  disabled={isUpdatingSmbRoot}
                  required
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="smb-root-base-path">
                  {t("detail.smbRoot.basePath")}
                </Label>
                <Input
                  id="smb-root-base-path"
                  value={smbRootBasePath}
                  onChange={(event) => setSmbRootBasePath(event.target.value)}
                  disabled={isUpdatingSmbRoot}
                  placeholder={t("detail.smbRoot.basePathPlaceholder")}
                />
              </div>
            </div>
            {smbRootUpdateError ? (
              <p className="text-sm font-medium text-destructive">
                {smbRootUpdateError}
              </p>
            ) : null}
            <p className="text-sm leading-6 text-amber-800">
              {t("detail.smbRoot.updateNotice")}
            </p>
            {isSmbRootHostChanged ? (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm leading-6 text-amber-900">
                {t("detail.smbRoot.hostChangeNotice")}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={isUpdatingSmbRoot}>
                {isUpdatingSmbRoot ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : null}
                {t("detail.smbRoot.save")}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelSmbRootForm}
                disabled={isUpdatingSmbRoot}
              >
                {t("detail.smbRoot.cancel")}
              </Button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <dl className="grid gap-3 md:grid-cols-3">
              <CredentialVerificationItem
                label={t("detail.smbRoot.host")}
                value={smbRoot.host || t("detail.smbRoot.empty")}
              />
              <CredentialVerificationItem
                label={t("detail.smbRoot.shareName")}
                value={smbRoot.shareName || t("detail.smbRoot.empty")}
              />
              <CredentialVerificationItem
                label={t("detail.smbRoot.basePath")}
                value={smbRoot.basePath || t("detail.smbRoot.rootPath")}
              />
            </dl>
            <Button
              type="button"
              variant="outline"
              onClick={handleOpenSmbRootForm}
            >
              <Pencil className="mr-2 h-4 w-4" />
              {t("detail.smbRoot.edit")}
            </Button>
          </div>
        )}
      </div>
    </ExpandablePanel>
  ) : null;

  const credentialPanel = isSmbScopedRuntimeCredential ? (
    <ExpandablePanel title={t("detail.credentials")} defaultOpen>
      <div className="space-y-5">
        <dl className="grid gap-3 md:grid-cols-3">
          <CredentialVerificationItem
            label={t("detail.credentialPanel.verificationResult")}
            value={credentialVerification.label}
            tone={credentialVerification.tone}
          />
          <CredentialVerificationItem
            label={t("detail.credentialPanel.lastVerifiedAt")}
            value={credentialLastVerifiedAt}
          />
          {credentialFailureReasonLabel ? (
            <CredentialVerificationItem
              label={t("detail.credentialPanel.lastFailureReason")}
              value={credentialFailureReasonLabel}
              tone="attention"
            />
          ) : null}
        </dl>

        {shouldRenderInlineCredentialForm ? null : (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="min-w-full">
              <p className="font-medium text-slate-950">
                {t("detail.credentialPanel.writeOnlyTitle")}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {credentialSummary?.credentialMaterialState === "configured" ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setCredentialFormOpen((value) => !value);
                    resetCredentialMaterialError();
                    resetCredentialVerifyError();
                    resetCredentialResetError();
                    setCredentialActionFeedback(null);
                  }}
                  disabled={credentialBusy}
                >
                  <KeyRound className="mr-2 h-4 w-4" />
                  {credentialFormOpen
                    ? t("detail.credentialPanel.closeForm")
                    : t("detail.credentialPanel.changeCredential")}
                </Button>
              ) : null}
              <Button
                type="button"
                variant="outline"
                onClick={() => void handleVerifySmbCredential()}
                disabled={
                  credentialBusy ||
                  credentialSummary?.credentialMaterialState !== "configured"
                }
              >
                {isVerifyingCredential ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                {t("detail.credentialPanel.verify")}
              </Button>
            </div>
            <div className="ml-auto">
              {canResetCredential ? (
                <Button
                  type="button"
                  variant="outline"
                  className={credentialDeleteButtonClassName}
                  onClick={() => setShowCredentialReset(true)}
                  disabled={credentialBusy}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("detail.credentialPanel.deleteCredential")}
                </Button>
              ) : null}
            </div>
          </div>

          {shouldShowSmbCredentialForm ? (
            <div className="mt-4">{smbCredentialFormElement}</div>
          ) : null}
        </div>
        )}

        {shouldRenderInlineCredentialForm ? null : (
          credentialActionFeedbackElement
        )}
      </div>
    </ExpandablePanel>
  ) : null;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/console/storages")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <StorageConnectionKindIconBadge
            connectionKindId={storage.connectionKindId}
            label={storageKindLabel(storage.connectionKindId, storage.providerFamilyDisplayLabel)}
            className="h-5 w-5"
            badgeClassName="h-10 w-10 rounded-lg"
          />
          <div className="min-w-0">
            {displayNameFormOpen ? (
              <form
                className="flex flex-wrap items-end gap-2"
                onSubmit={(event) => void handleSubmitStorageDisplayName(event)}
              >
                <div className="min-w-[16rem] max-w-sm space-y-1">
                  <Label htmlFor="storage-display-name">
                    {t("detail.displayNameLabel")}
                  </Label>
                  <Input
                    id="storage-display-name"
                    value={storageDisplayNameInput}
                    onChange={(event) => {
                      setStorageDisplayNameInput(event.target.value);
                      setStorageDisplayNameError(null);
                    }}
                    disabled={isUpdatingStorageDisplayName}
                    autoFocus
                  />
                </div>
                <Button
                  type="submit"
                  size="sm"
                  disabled={
                    isUpdatingStorageDisplayName ||
                    !storageDisplayNameInput.trim()
                  }
                >
                  {isUpdatingStorageDisplayName
                    ? t("detail.displayNameSaving")
                    : t("detail.displayNameSave")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleCancelDisplayNameForm}
                  disabled={isUpdatingStorageDisplayName}
                >
                  {t("detail.displayNameCancel")}
                </Button>
              </form>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="truncate text-2xl font-bold">{storage.displayName}</h1>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handleOpenDisplayNameForm}
                  aria-label={t("detail.displayNameEdit")}
                  title={t("detail.displayNameEdit")}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <span className="inline-flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">
                    {t("listStatusLabels.availability")}
                  </span>
                  <StatusBadge
                    state={AVAILABILITY_BADGE_STATE[availabilityCode]}
                    label={t(`listAvailabilityValues.${availabilityCode}`)}
                  />
                </span>
                {isReadinessRefreshing ? (
                  <Loader2
                    className="h-4 w-4 animate-spin text-sky-600"
                    aria-label={t("detail.readinessRefreshingLabel")}
                  />
                ) : null}
              </div>
            )}
          </div>
        </div>
        <StorageDeleteControl storage={storage} onDeleted={(gatewayCleanupNeeded) => {
          handoffMemory.forget(storageId);
          navigate("/console/storages", { state: { storageDeleted: true, gatewayCleanupNeeded } });
        }} />
      </div>

      <StorageUploadLimitForm key={storageId} storageId={storageId} />

      {refreshError ? (
        <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 text-sm text-amber-900">
          {t("detail.refreshError")}
        </div>
      ) : null}

      {storageDisplayNameError ?? storageDisplayNameUpdateError ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {storageDisplayNameError ?? storageDisplayNameUpdateError}
        </div>
      ) : null}

      {routeDeleteError ?? deleteRouteMutationError ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {routeDeleteError ?? deleteRouteMutationError}
        </div>
      ) : null}

      <Card>
        <CardContent className="space-y-5 p-6">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xl font-semibold text-slate-950">
                {t(needsCredentialInput ? "detail.credentialInputTitle" : `detail.actionTitles.${actionCode}`)}
              </h2>
              {hasPartialGatewayRouteAttention ? (
                <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                  {t("detail.partialGatewayRouteAttentionChip")}
                </span>
              ) : null}
            </div>
            {currentStatusBody ? (
              <p className="max-w-3xl text-sm leading-6 text-slate-600">
                {/* actionCode is a closed union from resolveStorageActionCode;
                    every member must have an actionBodies entry, so no fallback
                    is required. */}
                {currentStatusBody}
              </p>
            ) : null}
            {hasPartialGatewayRouteAttention ? (
              <p className="max-w-3xl text-sm leading-6 text-amber-800">
                {t("detail.partialGatewayRouteAttentionBody")}
              </p>
            ) : null}
            <div className="mt-3 flex max-w-3xl items-center gap-3">
              <div
                className={`grid min-w-0 flex-1 gap-3 ${
                  isLocalStorage ? "md:grid-cols-2" : "md:grid-cols-1"
                }`}
              >
                <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3">
                  <p className="text-xs font-medium text-slate-500">
                    {t("detail.settingStatusLabel")}
                  </p>
                  <div className="mt-1">
                    <StorageSettingStatusBadge state={storageSettingState} />
                  </div>
                </div>
                {isLocalStorage ? (
                  <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3">
                    <p className="text-xs font-medium text-slate-500">
                      {t("detail.currentRuntimeStatusLabel")}
                    </p>
                    <StatusBadge
                      className="mt-1"
                      state={runtimeStatusBadgeState(runtimeStatusLineCode)}
                      label={t(
                        needsCredentialInput
                          ? "detail.credentialInputStatus"
                          : actionCode === "configure_local_folder"
                            ? "detail.statusEvidenceValues.folderSelectionWaiting"
                            : actionCode === "approve_smb_relay"
                              ? "detail.statusEvidenceValues.relayApprovalWaiting"
                              : `runtimeStatusValues.${runtimeStatusLineCode}`,
                      )}
                    />
                    {runtimeStatusSnapshot?.source === "pf_unavailable" ? (
                      <p className="mt-2 text-sm leading-6 text-amber-800">
                        {t("detail.currentRuntimeStatusUnavailableHelp")}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <TooltipProvider delayDuration={150}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      aria-label={isManualRefreshBusy ? t("detail.rechecking") : t("detail.reloadCta")}
                      onClick={() => void handleRefresh()}
                      disabled={isManualRefreshBusy}
                    >
                      <RefreshCw className={`h-4 w-4${isManualRefreshBusy ? " animate-spin" : ""}`} aria-hidden="true" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {isManualRefreshBusy ? t("detail.rechecking") : t(isLocalStorage ? "detail.reloadSettingAndRuntime" : "detail.reloadSetting")}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            {storage.publicationSummary?.blockedReasonMessage ? (
              <div className="mt-3 max-w-3xl rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3">
                <p className="text-xs font-medium text-amber-800">
                  {t("detail.publicationBlockedReasonLabel")}
                </p>
                <p className="mt-1 text-sm leading-6 text-amber-900">
                  {t(`detail.publicationFailureReasons.${storage.publicationSummary.blockedReasonCode}`, { defaultValue: t("detail.publicationBlockedHelp") })}
                </p>
              </div>
            ) : null}
          </div>

          {statusEvidenceItems.length > 0 ? (
            <ExpandablePanel
              title={t("detail.statusDetails")}
              className="bg-slate-50/30"
            >
              <ol className="grid gap-3">
                {statusEvidenceItems.map((item, index) => (
                  <StatusEvidenceItem
                    key={item.key}
                    index={index + 1}
                    label={item.label}
                    value={item.value}
                    description={item.description}
                    tone={item.tone}
                  />
                ))}
              </ol>
            </ExpandablePanel>
          ) : null}

          {inlineCredentialEditor}

          {shouldShowRecoveryInstaller ? installerDownloadSection : null}

          {shouldShowRecoveryGuidance && shouldOfferHandoff && (canIssueGatewayRouteHandoff || gatewayRouteLimitReached) ? (
            <div className="flex flex-wrap gap-3">
              {canIssueGatewayRouteHandoff ? (
                <Button
                  onClick={() => void handleIssueSetupHandoff()}
                  disabled={isIssuingHandoff}
                >
                  {isIssuingHandoff
                    ? t("detail.localGatewaySetupIssuing")
                    : setupHandoffExpired
                      ? t("detail.localGatewaySetupReissueCta")
                      : t("detail.issueHandoff")}
                </Button>
              ) : gatewayRouteLimitReached ? (
                <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  {t("detail.gatewayRouteLimitReached")}
                </p>
              ) : null}
            </div>
          ) : null}

          {shouldShowRecoveryGuidance && !shouldOfferHandoff && shouldOfferAllGatewayRoutesDelete ? (
            <div className="flex flex-wrap gap-3">
              {shouldOfferAllGatewayRoutesDelete ? (
                <Button
                  variant="destructive"
                  onClick={() => {
                    resetDeleteAllRoutesError();
                    setRouteDeleteError(null);
                    setShowAllGatewayRoutesDelete(true);
                  }}
                  disabled={isDeletingAllRoutes}
                >
                  <Unlink2 className="mr-2 h-4 w-4" />
                  {t("detail.allGatewayRoutesDeleteCta")}
                </Button>
              ) : null}
            </div>
          ) : null}

          {shouldShowStandalonePollingIndicator ? (
            <div className="flex items-center gap-2 rounded-xl border border-sky-200 bg-sky-50/70 p-3 text-sm text-sky-800">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t("detail.pollingActive")}
            </div>
          ) : null}

          {shouldShowRecoveryGuidance && effectivePollingStatus === "timed_out" ? (
            <div className="space-y-2 rounded-xl border border-amber-200 bg-amber-50/70 p-3">
              <p className="text-sm font-medium text-amber-900">
                {t("detail.pollingTimedOut")}
              </p>
              <div className="flex flex-wrap gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={restartEffectivePolling}
                >
                  {t("detail.pollingRestart")}
                </Button>
              </div>
            </div>
          ) : null}

          {shouldShowRecoveryGuidance ? handoffResultPanel : null}

          {handoffError ? (
            <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
              {handoffError}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {requestLocalGatewayRouteSummary && hasGatewayRouteDeleteSelector ? (
        <GatewayRouteSummaryPanel
          summary={requestLocalGatewayRouteSummary}
          title={t("detail.gatewayRouteOperationsTitle")}
          maxRouteCount={gatewayRouteMaxCount ?? activeGatewayRouteCount}
          className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
          onDeleteRoute={handleOpenRouteDelete}
          deleteDisabled={isDeletingRoute}
          limitReachedMessage={
            storage.connectionKindId === "smb" &&
            gatewayRouteMaxCount !== null &&
            activeGatewayRouteCount > gatewayRouteMaxCount
              ? t("detail.gatewayRouteLimitReached")
              : undefined
          }
        />
      ) : null}


      <div className="space-y-3">
        {smbRootPanel}
        {credentialPanel}
        <ExpandablePanel title={t("detail.diagnostics")}>
          <div className="space-y-5 text-sm">
            <div
              className={`grid gap-4 ${
                isLocalStorage ? "lg:grid-cols-2" : "lg:grid-cols-1"
              }`}
            >
              {isLocalStorage ? (
                <section className="rounded-xl border border-slate-200 p-4">
                  <h3 className="text-base font-semibold text-slate-950">
                    {t("detail.stateGroups.localGateway")}
                  </h3>
                  <dl className="mt-3 space-y-2">
                    <DiagnosticValueItem
                      label={t("detail.localGatewaySetupStateShort")}
                      value={localGatewaySetupStateLabel}
                    />
                    {credentialStateLabel && !isSmbScopedRuntimeCredential ? (
                      <DiagnosticValueItem
                        label={t("detail.gatewayConnectionState")}
                        value={credentialStateLabel}
                      />
                    ) : null}
                    {requestLocalCurrentGatewayVersion ? (
                      <DiagnosticValueItem
                        label={t("detail.currentGatewayVersion")}
                        value={requestLocalCurrentGatewayVersion}
                      />
                    ) : null}
                  </dl>
                </section>
              ) : null}

              <section className="rounded-xl border border-slate-200 p-4">
                <h3 className="text-base font-semibold text-slate-950">
                  {t("detail.stateGroups.storage")}
                </h3>
                <dl className="mt-3 space-y-2">
                  <DiagnosticValueItem
                    label={t("detail.storageKind")}
                    value={storageKindLabel(storage.connectionKindId, storage.providerFamilyDisplayLabel)}
                  />
                  {shouldShowStorageDisplayLocation ? (
                    <DiagnosticValueItem
                      label={t("detail.storageDisplayLocation")}
                      value={storage.storageLocationDisplay.displayName}
                    />
                  ) : null}
                </dl>
              </section>
            </div>

            {warningCopies.length > 0 ? (
              <section>
                <p className="font-medium text-slate-950">
                  {t("detail.operabilityWarnings")}
                </p>
                <div className="mt-2 space-y-3">
                  {warningCopies.map((warning) => (
                    <div
                      key={warning.key}
                      className="rounded-xl border border-slate-200 bg-slate-50/70 p-3"
                    >
                      <p className="font-medium text-slate-950">{warning.title}</p>
                      <p className="mt-1 text-sm leading-6 text-slate-600">
                        {warning.body}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            {shouldShowTechnicalDetails ? (
              <section className="border-t border-slate-200 pt-5">
                <h3 className="text-base font-semibold text-slate-950">
                  {t("detail.technicalDetails")}
                </h3>
                <dl className="mt-4 grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
                  {shouldShowPublicationTechnicalDetails &&
                  storage.publicationSummary?.desiredPublicationMode ? (
                    <div>
                      <dt className="text-muted-foreground">
                        {t("detail.desiredPublicationMode")}
                      </dt>
                      <dd className="mt-1 font-medium">
                        {desiredPublicationModeLabel}
                      </dd>
                    </div>
                  ) : null}
                  {shouldShowPublicationTechnicalDetails &&
                  storage.publicationSummary?.effectivePublicationMode ? (
                    <div>
                      <dt className="text-muted-foreground">
                        {t("detail.effectivePublicationMode")}
                      </dt>
                      <dd className="mt-1 font-medium">
                        {effectivePublicationModeLabel}
                      </dd>
                    </div>
                  ) : null}
                  {shouldShowPublicationTechnicalDetails &&
                  storage.publicationSummary?.effectivePublishedOrigin ? (
                    <div className="md:col-span-2">
                      <dt className="text-muted-foreground">
                        {t("detail.publicationOrigin")}
                      </dt>
                      <dd className="mt-1 break-all font-medium">
                        {storage.publicationSummary.effectivePublishedOrigin}
                      </dd>
                    </div>
                  ) : null}
                  {shouldShowGatewayVersionTechnicalDetails &&
                  storage.requiredGatewayVersionRange ? (
                    <div>
                      <dt className="text-muted-foreground">
                        {t("detail.requiredGatewayVersionRange")}
                      </dt>
                      <dd className="mt-1 font-medium">
                        {storage.requiredGatewayVersionRange}
                      </dd>
                    </div>
                  ) : null}
                  {shouldShowGatewayVersionTechnicalDetails &&
                  requestLocalCurrentGatewayVersion ? (
                    <div>
                      <dt className="text-muted-foreground">
                        {t("detail.currentGatewayVersion")}
                      </dt>
                      <dd className="mt-1 font-medium">
                        {requestLocalCurrentGatewayVersion}
                      </dd>
                    </div>
                  ) : null}
                </dl>
              </section>
            ) : null}
          </div>
        </ExpandablePanel>
      </div>

      {isLocalStorage && (!shouldShowRecoveryInstaller || shouldShowDay2Handoff) ? <div className="space-y-3 border-t pt-4">
        {!shouldShowRecoveryInstaller ? installerDownloadSection : null}
      {shouldShowDay2Handoff ? (
        <section>
          <Button type="button" variant="link" className="h-auto px-0 text-muted-foreground"
            aria-expanded={gatewayAddOpen} aria-controls="gateway-add-panel"
            onClick={() => setGatewayAddOpen(open => !open)}>
            {gatewayAddOpen ? <ChevronDown className="mr-2 h-4 w-4" aria-hidden="true" /> : <ChevronRight className="mr-2 h-4 w-4" aria-hidden="true" />}
            {t("detail.gatewayAddToggle")}
          </Button>
          {gatewayAddOpen ? <div id="gateway-add-panel" className="space-y-4 pt-3">
            <p className="text-sm leading-6 text-slate-600">
              {t("detail.localGatewayRouteAddBody")}
            </p>
            <div className="flex flex-wrap gap-3">
              {canIssueGatewayRouteHandoff ? (
                <Button
                  onClick={() => void handleIssueSetupHandoff()}
                  disabled={isIssuingHandoff}
                >
                  {isIssuingHandoff
                    ? t("detail.localGatewaySetupIssuing")
                    : setupHandoffExpired
                      ? t("detail.localGatewaySetupReissueCta")
                      : t("detail.localGatewayRouteAddCta")}
                </Button>
              ) : null}
            </div>
            {handoffResultPanel}
            {handoffError ? (
              <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
                {handoffError}
              </div>
            ) : null}
          </div> : null}
        </section>
      ) : null}
      </div> : null}

      <ConfirmDialog
        open={routeDeleteTarget !== null}
        onClose={() => {
          if (!isDeletingRoute) {
            setRouteDeleteTarget(null);
            setRouteDeleteError(null);
            resetDeleteRouteError();
          }
        }}
        onConfirm={() => handleConfirmRouteDelete()}
        title={t("detail.gatewayRouteDeleteTitle")}
        description={
          routeDeleteTarget ? (
            <div className="space-y-2">
              <p>{t("detail.gatewayRouteDeleteDescription")}</p>
              <p className="text-sm font-medium text-slate-950">
                {t("detail.gatewayRouteDeleteTarget", {
                  name:
                    routeDeleteTarget.gatewayDisplayName ??
                    t("detail.gatewayRouteUnnamedGateway"),
                })}
              </p>
            </div>
          ) : undefined
        }
        matchValue={
          routeDeleteTarget
            ? t("detail.gatewayRouteDeleteConfirmValue")
            : undefined
        }
        matchLabel={t("detail.gatewayRouteDeleteConfirmLabel")}
        isLoading={isDeletingRoute}
        variant="destructive"
        confirmText={t("detail.gatewayRouteDeleteCta")}
      />
      <ConfirmDialog
        open={showAllGatewayRoutesDelete}
        onClose={() => {
          if (!isDeletingAllRoutes) {
            setShowAllGatewayRoutesDelete(false);
            setRouteDeleteError(null);
            resetDeleteAllRoutesError();
          }
        }}
        onConfirm={() => handleConfirmAllGatewayRoutesDelete()}
        title={t("detail.allGatewayRoutesDeleteTitle")}
        description={
          <div className="space-y-2">
            <p>{t("detail.allGatewayRoutesDeleteDescription")}</p>
            <p>{t("detail.allGatewayRoutesDeleteScope")}</p>
          </div>
        }
        matchValue={storage.displayName}
        matchLabel={t("detail.allGatewayRoutesDeleteConfirmLabel")}
        isLoading={isDeletingAllRoutes}
        variant="destructive"
        confirmText={t("detail.allGatewayRoutesDeleteCta")}
      />
      <ConfirmDialog
        open={showCredentialReset}
        onClose={() => {
          if (!isResettingCredential) {
            setShowCredentialReset(false);
            resetCredentialResetError();
          }
        }}
        onConfirm={() => handleResetSmbCredential()}
        title={t("detail.credentialPanel.deleteTitle")}
        description={t("detail.credentialPanel.deleteDescription")}
        isLoading={isResettingCredential}
        variant="destructive"
        confirmText={t("detail.credentialPanel.deleteCredential")}
      />
    </div>
  );
}

export function StorageDetailPage() {
  const { storageId } = useParams<{ storageId: string }>();

  if (!storageId) {
    return <Navigate to="/console/storages" replace />;
  }

  return <StorageDetailContent key={storageId} storageId={storageId} />;
}
